// src/pages/HistoryDetail.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useHistoryApi } from '../api/history';
import { generateItineraryPDF } from '../utils/pdfGenerator';
import logo from '../assets/vertikalogo.jpg';

import ChatHeader      from '../components/ChatHeader';
import ItineraryScreen from '../components/ItineraryScreen';
import LoaderScreen    from '../components/LoaderScreen';
import ErrorScreen     from '../components/ErrorScreen';
import Layout          from '../components/Layout';

export default function HistoryDetail() {
  const { fetchHistoryList, fetchHistoryItem } = useHistoryApi();
  const { chatId } = useParams();
  const navigate   = useNavigate();

  const [historyList, setHistoryList] = useState([]);
  const [entry, setEntry]             = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingEntry, setLoadingEntry] = useState(true);
  const [errorList, setErrorList]     = useState(false);
  const [errorEntry, setErrorEntry]   = useState(false);
  const detailRef = useRef(null);

  // fetch full history list once
  useEffect(() => {
    let mounted = true;
    fetchHistoryList()
      .then(data => {
        if (!mounted) return;
        setHistoryList(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!mounted) return;
        setErrorList(true);
      })
      .finally(() => {
        if (!mounted) return;
        setLoadingList(false);
      });
    return () => { mounted = false; };
  }, []);

  // fetch specific entry when chatId changes
  useEffect(() => {
    if (!chatId) {
      setEntry(null);
      setLoadingEntry(false);
      return;
    }
    let mounted = true;
    setLoadingEntry(true);
    fetchHistoryItem(chatId)
      .then(data => {
        if (!mounted) return;
        setEntry(data);
      })
      .catch(() => {
        if (!mounted) return;
        setErrorEntry(true);
      })
      .finally(() => {
        if (!mounted) return;
        setLoadingEntry(false);
      });
    return () => { mounted = false; };
  }, [chatId]);

  const handleDownload = () => {
  if (!entry) return;

  const printContainer = document.createElement('div');
  printContainer.style.width = '800px';
  printContainer.style.padding = '24px';
  printContainer.style.fontSize = '12pt';
  printContainer.style.lineHeight = '1.5';
  printContainer.style.color = '#000';
  printContainer.style.background = '#fff';
  printContainer.style.fontFamily = `'Helvetica Neue', Helvetica, Arial, sans-serif`;
  printContainer.innerHTML = entry.response
    .replace(/(^|\r?\n)\s*---+\s*(?=\r?\n|$)/g, '<hr style="margin: 1rem 0; border: none; border-top: 1px solid #ccc;" />')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^#{1,6}\s*(.*)$/gim, '<h3 style="margin: 1rem 0 0.5rem; font-size: 16px;">$1</h3>')
    .replace(/^\s*[-*]\s+(.*)$/gm, '<li>$1</li>')
    .replace(/(?:<li>[\s\S]*?<\/li>)+/g, (m) => `<ul style="padding-left: 1.2rem; margin-bottom: 1rem;">${m}</ul>`)
    .replace(/\n{2,}/g, '<p style="margin: 0.75rem 0;"></p>')
    .replace(/\n/g, '<br />')
    .replace(/\*/g, '');

  document.body.appendChild(printContainer);
  generateItineraryPDF(printContainer, logo, entry.destination.trim().replace(/[^\w\s]/g, '').replace(/\s+/g, '_').toLowerCase() + '.pdf');
  setTimeout(() => {
    document.body.removeChild(printContainer);
  }, 1000);
};


  const sidebarContent = (
    <div className="h-full flex flex-col">
      <h2 className="px-4 py-3 text-lg font-semibold border-b">
        Itinerary History
      </h2>
      {loadingList ? (
        <LoaderScreen index={0} />
      ) : errorList ? (
        <p className="p-4 text-red-500">Failed to load history.</p>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {historyList.map(item => (
            <div
              key={item.chatId}
              onClick={() => navigate(`/history/${item.chatId}`)}
              className={`px-4 py-3 cursor-pointer border-b last:border-0 ${
                item.chatId === chatId ? 'bg-blue-100 font-medium' : 'hover:bg-gray-100'
              }`}
            >
              <div className="text-sm">{item.destination}</div>
              <div className="text-xs text-gray-500">
                {new Date(item.createdAt).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  let mainContent;
  if (loadingEntry) {
    mainContent = <LoaderScreen index={0} />;
  } else if (errorEntry) {
    mainContent = <ErrorScreen onBack={() => navigate(-1)} />;
  } else if (entry) {
    mainContent = (
      <div ref={detailRef} className="relative min-h-screen w-full bg-gradient-to-br from-blue-50 to-green-50">
        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-10">
          <div className="bg-white bg-opacity-95 shadow-2xl rounded-2xl w-full max-w-3xl flex flex-col overflow-hidden">
            <ChatHeader hasItinerary onDownload={handleDownload} />
            <ItineraryScreen content={entry.response} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout sidebar={sidebarContent}>
      {mainContent}
    </Layout>
  );
}
